import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllKebabs,
  getSingleKebab,
  createKebab,
  updateKebab,
  deleteKebab,
} from "@/api/new-api/staff-management/menu-manage/kebab";
import { websiteOrderingAvailableItemsToggle } from "@/api/new-api/staff-management/menu-manage/drink";

// Thunks
export const fetchAllKebabs = createAsyncThunk(
  "kebabs/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllKebabs(restaurant_id);
    if (res.status === "success") return res.data.kebabs;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleKebab = createAsyncThunk(
  "kebabs/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleKebab(id);
    if (res.status === "success") return res.data.kebab;
    return rejectWithValue(res.message);
  }
);

export const createNewKebab = createAsyncThunk(
  "kebabs/create",
  async (data, { rejectWithValue }) => {
    const res = await createKebab(data);
    if (res.status === "success") return res.data.kebab;
    return rejectWithValue(res.message);
  }
);

export const updateExistingKebab = createAsyncThunk(
  "kebabs/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateKebab(id, data);
    if (res.status === "success") return res.data.kebab;
    return rejectWithValue(res.message);
  }
);

export const deleteKebabById = createAsyncThunk(
  "kebabs/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteKebab(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

export const updateKebabToggle = createAsyncThunk("kebabToggle/update", async ({ id, type, toggleAvailabilityType }, { rejectWithValue }) => {
    const res = await websiteOrderingAvailableItemsToggle(id, type, toggleAvailabilityType);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
});

// Initial state
const initialState = {
  kebabs: [],
  singleKebab: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice
const kebabSlice = createSlice({
  name: "kebabs",
  initialState,
  reducers: {
    clearKebabMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllKebabs.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllKebabs.fulfilled, (state, action) => {
        state.getLoading = false;
        state.kebabs = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllKebabs.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch One
      .addCase(fetchSingleKebab.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleKebab.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleKebab = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleKebab.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewKebab.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewKebab.fulfilled, (state, action) => {
        state.createLoading = false;
        state.kebabs.push(action.payload);
        state.success = "Kebab created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewKebab.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingKebab.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingKebab.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.kebabs = state.kebabs.map((kebab) =>
          kebab.id === action.payload.id ? action.payload : kebab
        );
        state.success = "Kebab updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingKebab.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteKebabById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteKebabById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.kebabs = state.kebabs.filter(
          (kebab) => kebab.id !== action.payload
        );
        state.success = "Kebab deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteKebabById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // toggle website/QRcode availability
      .addCase(updateKebabToggle.pending, (state) => {
          state.updateLoading = true;
          state.error = null;
          state.requestStatus = false;
      })
      .addCase(updateKebabToggle.fulfilled, (state, action) => {
        state.updateLoading = false;

        const { id, toggleAvailabilityType } = action.meta.arg;

        state.kebabs = state.kebabs.map((drink) => {
          if (drink.id !== id) return drink;

          if (toggleAvailabilityType === "qr_code") {
            return {
              ...drink,
              is_qr_code_order_available: !drink.is_qr_code_order_available,
            };
          }

          return {
            ...drink,
            is_website_order_available: !drink.is_website_order_available,
          };
        });

        state.success = "Updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateKebabToggle.rejected, (state, action) => {
          state.updateLoading = false;
          state.error = action.payload;
          state.requestStatus = false;
      })
  },
});

export const { clearKebabMessages } = kebabSlice.actions;
export default kebabSlice.reducer;
